import React, { useEffect, useState } from 'react';
import classnames from 'clsx';
import PropTypes from 'prop-types';

import { Text, TextType } from 'ui-components/components/text/index';
import { noop } from 'ui-components/default-props/noop';

import { getMappedSellerProviderList } from './provider-mapping-service';
import { getSortedSellerLabelProviderList } from './provider-sort-service';
import { SellerProviderListItem } from './seller-provider-list-item';
import style from './seller-provider-list.module.css';
import { emptyArray } from 'ui-components/default-props/empty';
import { useProviderServices } from '../../../hooks/use-provider-services';
import { LabelProviderPropType } from '../../../prop-types/label-provider';
import { RateBrowserProviderPropType } from '../../../prop-types/rate-browser-provider';
import { messages } from '../messages';

const useSortedSellerLabelProviders = (sellerLabelProviders, sellerProviderRates) => {
  const providerServices = useProviderServices();
  const mappedSellerProviderList = getMappedSellerProviderList(sellerLabelProviders, sellerProviderRates, providerServices);
  return getSortedSellerLabelProviderList(mappedSellerProviderList);
};

export const NewSellerProviderList = ({
  selectedSellerProviderId = '',
  sellerProviderRates = emptyArray,
  onSellerProviderClicked = noop,
  className = '',
  sellerLabelProviders,
}) => {
  const sortedSellerLabelProviders = useSortedSellerLabelProviders(sellerLabelProviders, sellerProviderRates);

  const [initialClick, setInitialClick] = useState(false);

  useEffect(() => {
    const someWereLoading = sellerProviderRates.some((rate) => rate.isLoading);
    if (initialClick && !someWereLoading) {
      return;
    }

    if (someWereLoading) {
      setInitialClick(false);
      return;
    }

    if (sortedSellerLabelProviders.length) {
      onSellerProviderClicked(sortedSellerLabelProviders[0].sellerLabelProviderId);
    }
    setInitialClick(true);
  }, [initialClick, setInitialClick, sellerProviderRates, sortedSellerLabelProviders, onSellerProviderClicked]);

  // Separate service collections from traditional providers
  const serviceCollections = sortedSellerLabelProviders.filter((provider) => provider.isServiceCollection);
  const traditionalProviders = sortedSellerLabelProviders.filter((provider) => !provider.isServiceCollection);

  return (
    <div className={classnames(style['seller-provider-list'], className)}>
      {serviceCollections.length > 0 && (
        <div className={style['provider-section']}>
          <Text className={style['section-header']} textType={TextType.Caption} value={messages.automatedRatesSectionHeader} />
          {serviceCollections.map((mappedSellerProvider) => (
            <SellerProviderListItem
              key={mappedSellerProvider.sellerLabelProviderId}
              className={style['seller-provider-list-item']}
              isLoading={mappedSellerProvider.isLoading}
              isServiceCollection={mappedSellerProvider.isServiceCollection}
              name={mappedSellerProvider.name || mappedSellerProvider.providerDisplayInformation?.name}
              numberOfValidRates={mappedSellerProvider.rates?.length ?? 0}
              onClick={onSellerProviderClicked}
              providerId={Number.parseInt(mappedSellerProvider.labelProviderId, 10)}
              selected={selectedSellerProviderId === mappedSellerProvider.sellerLabelProviderId}
              sellerLabelProviderId={mappedSellerProvider.sellerLabelProviderId}
            />
          ))}
        </div>
      )}

      {traditionalProviders.length > 0 && (
        <div className={style['provider-section']}>
          <Text className={style['section-header']} textType={TextType.Caption} value={messages.carrierAccountsSectionHeader} />
          {traditionalProviders.map((mappedSellerProvider) => (
            <SellerProviderListItem
              key={mappedSellerProvider.sellerLabelProviderId}
              className={style['seller-provider-list-item']}
              isLoading={mappedSellerProvider.isLoading}
              name={mappedSellerProvider.name || mappedSellerProvider.providerDisplayInformation?.name}
              numberOfValidRates={mappedSellerProvider.rates?.length ?? 0}
              onClick={onSellerProviderClicked}
              providerId={Number.parseInt(mappedSellerProvider.labelProviderId, 10)}
              selected={selectedSellerProviderId === mappedSellerProvider.sellerLabelProviderId}
              sellerLabelProviderId={mappedSellerProvider.sellerLabelProviderId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

NewSellerProviderList.propTypes = {
  selectedSellerProviderId: PropTypes.string,
  sellerProviderRates: PropTypes.arrayOf(RateBrowserProviderPropType),
  onSellerProviderClicked: PropTypes.func,
  className: PropTypes.string,
  sellerLabelProviders: PropTypes.arrayOf(LabelProviderPropType).isRequired,
};
